/**
 * Kabex Motors business facts, as supplied in the brief (§6, §14). Every
 * component reads them from here; none may retype them.
 */

/**
 * What a number can actually be reached on.
 *
 * It exists because "callable" was being inferred from the country. Three
 * components filtered these numbers with `country === 'Uganda'` to mean "a
 * line a visitor may ring", which worked only for as long as the one
 * international number happened to be the one that takes no calls. The next
 * number added would have inherited whichever behaviour its country implied,
 * silently. So the intent is stated per number instead, and every consumer
 * reads it rather than guessing from the dialling code.
 *
 * `call` puts the number in a `tel:` link. `whatsapp` puts it behind a
 * `wa.me` link. A number may be both; one that is neither is not published.
 */
export type PhoneChannel = 'call' | 'whatsapp';

export type PhoneNumber = {
  /**
   * Stable handle for this line, so other config can point at it without
   * retyping the digits or matching on a country name. See the `japan` seat
   * in `config/whatsappContacts.ts`.
   */
  id: string;
  country: string;
  display: string;
  /** E.164, used for tel: and wa.me links. */
  e164: string;
  /** How this number may be reached. Never empty. */
  channels: readonly PhoneChannel[];
  /**
   * What the line is for, when that is not obvious from the number. Shown to
   * a visitor beside any number that is not simply callable, because
   * "WhatsApp only" raises the question this answers.
   */
  note?: string;
};

export type DayOfWeek =
  'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
/** One run of days sharing the same hours. Times are 24h `HH:mm`, Kampala time. */
export type OpeningHours = {
  label: string;
  days: readonly DayOfWeek[];
  opens: string;
  closes: string;
};

export type SocialPlatform = 'facebook' | 'instagram' | 'youtube' | 'x' | 'linkedin' | 'tiktok';
/** A null `url` hides that icon, so placeholders are safe to ship. */
export type SocialLink = { platform: SocialPlatform; label: string; url: string | null };

/**
 * The geocoder string for the office, and the two Google URLs built from it.
 *
 * One string, two destinations: the `Get directions` button and the embedded
 * map on /contact. They have to agree, because a map pinned on one spot beside
 * a button that routes to another is worse than either on its own. So the query
 * is written once here and encoded twice below.
 *
 * It is a search string rather than the `address` fields: a geocoder wants the
 * postal line and chokes on our room numbers and P.O. box, and `Plot No. 4A`
 * reads to Google as `Plot 4A`. Changing it moves the pin and the route
 * together, which is the point.
 */
const MAP_QUERY = 'Crown House Building, Plot 4A Kampala Road, Kampala, Uganda';

export const company = {
  brandName: 'Kabex Motors',
  legalName: 'Kabex Motors Enterprise Ltd.',
  established: 2018,
  incorporated: 2020,
  motto: "Don't dream it, drive it.",
  mission: 'To deliver consistently excellent experiences for each customer every day.',
  vision: 'To be the world’s most exciting leader in automotive business.',
  values: ['Transparency', 'Reliability', 'Honesty', 'Customer Priority', 'Teamwork'],

  /** Sales inbox, published alongside the general contact address. */
  email: 'sales@kabexmotors.com',
  /** General enquiries and service questions. */
  inquiryEmail: 'info@kabexmotors.com',
  address: {
    street: 'Plot No. 4A Kampala Road',
    building: 'Crown House Building',
    unit: 'Ground Floor, Room 09 and Room 12',
    poBox: 'P.O. Box 2942',
    city: 'Kampala',
    country: 'Uganda',
    countryCode: 'UG',
  },
  /**
   * The published lines, each with the channels it answers on.
   *
   * The Japan desk takes messages and not calls (decided 2026-10-24). It is a
   * sourcing desk in another time zone, nine hours ahead of Kampala, and a
   * visitor who rang it reached the wrong half of the business at the wrong
   * hour. It is still published — it is evidence that Kabex has someone at
   * the source — but it is published as a WhatsApp line, which is what it is.
   */
  phones: [
    {
      id: 'ug-mobile',
      country: 'Uganda',
      display: '+256 780 905 191',
      e164: '+256780905191',
      channels: ['call', 'whatsapp'],
    },
    {
      id: 'ug-office',
      country: 'Uganda',
      display: '+256 (0) 200 926 530',
      e164: '+256200926530',
      /* Call only, which is what the site already did with it: no WhatsApp
         control has ever opened this number. */
      channels: ['call'],
    },
    {
      id: 'jp-sourcing',
      country: 'Japan',
      display: '+81 70-5623-2443',
      e164: '+817056232443',
      channels: ['whatsapp'],
      note: 'WhatsApp only. This is our sourcing desk in Japan, nine hours ahead of Kampala, so it takes messages rather than calls.',
    },
  ] satisfies PhoneNumber[],

  /** Office hours (confirmed 2026-09-24). Days not listed are closed. */
  openingHours: [
    {
      label: 'Mon – Fri',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
    { label: 'Saturday', days: ['Saturday'], opens: '08:00', closes: '14:00' },
  ] satisfies OpeningHours[],
  closedNote: 'Closed on Sundays and public holidays',

  /** Google Maps directions to the office, for a `Get directions` link. */
  directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(MAP_QUERY)}`,

  /**
   * The same place as a map to embed in an `iframe`.
   *
   * `output=embed` on the classic `maps.google.com` path is the keyless embed:
   * it needs no Google Cloud project and no API key, which is why the live map
   * on /contact can ship from a static host with no build-time secret. The
   * trade is that it is undocumented, so Google owes us no notice if it
   * changes. If it ever stops rendering, the replacement is the Maps Embed API
   * (`/maps/embed/v1/place`), which is the supported path and wants a
   * referrer-restricted key in the environment.
   *
   * `z=16` is street level: close enough that Kampala Road and the turning are
   * both legible, wide enough that the block still has context around it.
   */
  mapEmbedUrl: `https://maps.google.com/maps?q=${encodeURIComponent(MAP_QUERY)}&z=16&hl=en&ie=UTF8&iwloc=B&output=embed`,

  /**
   * The general WhatsApp line (A21, confirmed 2026-09-17).
   *
   * Two jobs. It is the `main` seat in `config/whatsappContacts.ts` — the
   * number any page with no seat of its own opens, and the one a seat waiting
   * on a number falls back to — and setting it to `null` is still the kill
   * switch that removes every WhatsApp control on the site at once, since an
   * empty main seat resolves to no link anywhere.
   *
   * It is no longer the only number the site can dial. Per-page numbers are
   * declared in `config/whatsappContacts.ts`; nothing is routed by editing
   * this field.
   */
  whatsappE164: '+256780905191' as string | null,

  /** TODO: fill in the real profile URLs. */
  socials: [
    { platform: 'tiktok', label: 'Tiktok', url: 'https://www.tiktok.com/@kabexmotorsltd' },
    {
      platform: 'linkedin',
      label: 'LinkedIn',
      url: 'https://www.linkedin.com/company/kabex-motors',
    },
    {
      platform: 'facebook',
      label: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61592821882905',
    },
    { platform: 'x', label: 'X', url: 'https://x.com/KabexMotorsLtd' },
    { platform: 'youtube', label: 'YouTube', url: 'https://www.youtube.com/@KabexMotorsLtdUG' },
  ] satisfies SocialLink[],
} as const;

/**
 * The ids of the published lines, as a union rather than `string`.
 *
 * Derived, so it cannot fall out of step with the array above: pointing other
 * config at a line that no longer exists is a build error. See the `japan`
 * seat in `config/whatsappContacts.ts`.
 */
export type PhoneId = (typeof company.phones)[number]['id'];
